import React, { useEffect, useState } from "react";
import { ReasonDialog } from "@/components/BookingStatus";
import { toast } from "sonner";
import { Check, X, Trash2 } from "lucide-react";
import { Stars } from "@/components/SpotReviews";
import { api, errorText } from "@/lib/api";
import type { SpotReview } from "@/lib/spots";
import { usePrefs } from "@/contexts/PrefsContext";

type St = "pending" | "approved" | "rejected";

export const AdminSpotReviews = ({ canEdit }: { canEdit: boolean }) => {
  const { t } = usePrefs();
  const [st, setSt] = useState<St>("pending");
  const [rows, setRows] = useState<SpotReview[] | null>(null);
  const load = () => api.get("/admin/spot-reviews", { params: { status: st } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    let live = true;
    setRows(null);
    api.get("/admin/spot-reviews", { params: { status: st } }).then((r) => live && setRows(r.data)).catch(() => live && setRows([]));
    return () => { live = false; };
  }, [st]);

  const [rejecting, setRejecting] = useState<SpotReview | null>(null);
  const act = async (r: SpotReview, a: "approve" | "reject" | "delete", reason = "") => {
    if (a === "reject" && !reason) return setRejecting(r);
    setRejecting(null);
    if (a === "delete" && !window.confirm(t("rev.confirmDelete"))) return;
    try {
      await (a === "delete" ? api.delete(`/admin/spot-reviews/${r.review_id}`) : api.post(`/admin/spot-reviews/${r.review_id}/${a}`, { reason }));
      toast.success(t(`rev.done.${a}`)); load();
    } catch (e) { toast.error(errorText(e, t)); }
  };
  const btn = "h-9 w-9 grid place-items-center rounded-full";

  return (
    <div data-testid="admin-spot-reviews" className="space-y-4">
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {(["pending", "approved", "rejected"] as St[]).map((k) => (
          <button key={k} data-testid={`admin-reviews-filter-${k}`} aria-pressed={st === k} onClick={() => setSt(k)}
            className={`h-9 px-4 rounded-full text-sm font-bold ${st === k ? "bg-foreground text-background" : "bg-card border"}`}>{t(`rev.status.${k}`)}</button>
        ))}
      </div>
      {rows?.length === 0 && <p data-testid="admin-reviews-empty" className="text-center text-muted-foreground py-12">{t("rev.adminEmpty")}</p>}
      <div className="grid gap-2 lg:grid-cols-2 items-start">
      {rows?.map((r) => (
        <div key={r.review_id} data-testid={`admin-review-${r.review_id}`} className="rounded-2xl border bg-card p-3 flex gap-3 items-start">
          <div className="flex-1 min-w-0 space-y-1">
            <p className="text-xs font-bold text-muted-foreground truncate">{[r.spot_name_ar, r.author].filter(Boolean).join(" · ")}</p>
            <Stars value={r.rating} />
            {r.tip && <p className="text-sm line-clamp-2">{r.tip}</p>}
            {r.reject_reason && <p className="text-xs text-rose-600">{t("rev.reason")}: {r.reject_reason}</p>}
          </div>
          {canEdit && <div className="flex gap-1.5">
            {r.status !== "approved" && <button data-testid={`admin-review-approve-${r.review_id}`} onClick={() => act(r, "approve")} className={`${btn} bg-emerald-100 text-emerald-700`} title={t("rev.approve")}><Check className="h-4 w-4" /></button>}
            {r.status !== "rejected" && <button data-testid={`admin-review-reject-${r.review_id}`} onClick={() => act(r, "reject")} className={`${btn} bg-amber-100 text-amber-700`} title={t("rev.reject")}><X className="h-4 w-4" /></button>}
            <button data-testid={`admin-review-delete-${r.review_id}`} onClick={() => act(r, "delete")} className={`${btn} bg-rose-50 text-rose-600`}><Trash2 className="h-4 w-4" /></button>
          </div>}
        </div>
      ))}
      </div>
      {rejecting && <ReasonDialog testid="admin-review-reject-dialog" title={t("rev.reject")} note={<span className="line-clamp-3">{rejecting.tip || rejecting.author}</span>} onClose={() => setRejecting(null)} onSubmit={(x) => act(rejecting, "reject", x)} />}
    </div>
  );
};
